import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cx, fieldControlClass } from './shared';

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  wrapperClassName?: string;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      label,
      description,
      error,
      id,
      required,
      className,
      wrapperClassName,
      'aria-describedby': ariaDescribedBy,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? `field-${generatedId}`;
    const descriptionId = description ? `${inputId}-description` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, descriptionId, errorId].filter(Boolean).join(' ');

    return (
      <div className={cx('grid gap-field-gap', wrapperClassName)}>
        <label htmlFor={inputId} className="text-control font-semibold text-ink-strong">
          {label}
          {required ? <span className="text-status-invalid"> *</span> : null}
        </label>
        {description ? (
          <p id={descriptionId} className="text-meta text-ink-muted">
            {description}
          </p>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={describedBy || undefined}
          className={cx(fieldControlClass, className)}
          {...rest}
        />
        {error ? (
          <p id={errorId} className="text-meta font-semibold text-status-invalid">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);

Field.displayName = 'Field';
